import { useState, useCallback, useRef } from 'react';
import productSizesService from '../services/productSizesServices';
import { useMessageAlert } from '../components/common/MessageAlert';
import i18n from '../i18n';

/**
 * Custom hook for managing product sizes data and operations
 */
const useProductSizes = () => {
  const [productSizes, setProductSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  // Deduplication refs
  const lastSignatureRef = useRef('');
  const lastFetchTimeRef = useRef(0);
  // Track in-flight requests to avoid duplicate network calls (useful in StrictMode)
  const inFlightRef = useRef(new Set());
  // Message alerts
  const { showCreate, showUpdate, showDelete } = useMessageAlert();

  /**
   * Generate request signature for deduplication
   */
  const generateSignature = useCallback((params) => {
    return JSON.stringify(params || {});
  }, []);

  /**
   * Check if request should be deduplicated
   */
  const shouldDedupe = useCallback((signature, force) => {
    if (force) return false;
    
    const now = Date.now();
    const timeDiff = now - lastFetchTimeRef.current;
    
    return signature === lastSignatureRef.current && timeDiff < 200;
  }, []);

  /**
   * Fetch all product sizes with optional filters
   */
  const fetchProductSizes = useCallback(async (params = {}, options = {}) => {
    const { force = false } = options;
    const signature = generateSignature(params);
    
    if (shouldDedupe(signature, force)) {
      return { success: true, fromCache: true };
    }

    // If an identical request is already in-flight, skip issuing another one
    if (inFlightRef.current.has(signature)) {
      return { success: true, fromCache: true };
    }

    setLoading(true);
    setError(null);
    // mark this signature as in-flight
    inFlightRef.current.add(signature);
    
    try {
      const result = await productSizesService.getAllProductSizes(params, options);
      
      if (result.success) {
        const { data } = result;
        
        // Handle different response structures
        if (data?.product_sizes && Array.isArray(data.product_sizes)) {
          setProductSizes(data.product_sizes);
          setTotalPages(data.totalPages || Math.ceil((data.total || data.product_sizes.length) / (params.limit || 20)));
          setTotalCount(data.total || data.product_sizes.length);
        } else if (data?.items && Array.isArray(data.items)) {
          setProductSizes(data.items);
          setTotalPages(data.totalPages || Math.ceil((data.total || data.items.length) / (params.limit || 20)));
          setTotalCount(data.total || data.items.length);
        } else if (Array.isArray(data)) {
          setProductSizes(data);
          setTotalPages(1);
          setTotalCount(data.length);
        } else {
          console.warn('Unexpected product sizes data structure:', data);
          setProductSizes([]);
          setTotalPages(0);
          setTotalCount(0);
        }
        
        lastSignatureRef.current = signature;
        lastFetchTimeRef.current = Date.now();
      } else {
        setError(result.error);
        setProductSizes([]);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      setProductSizes([]);
      return { success: false, error: errorMessage };
    } finally {
      // clear in-flight marker
      inFlightRef.current.delete(signature);
      setLoading(false);
    }
  }, [generateSignature, shouldDedupe]);

  /**
   * Get a specific product size by ID
   */
  const getProductSizeById = useCallback(async (productSizeId) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await productSizesService.getProductSizeById(productSizeId);
      
      if (!result.success) {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new product size
   */
  const createProductSize = useCallback(async (productSizeData) => {
    setError(null);
    
    try {
      const result = await productSizesService.createProductSize(productSizeData);
      if (result.success) {
        // Add the new product size to the local state
        setProductSizes(prevProductSizes => [result.data, ...prevProductSizes]);
        setTotalCount(prevCount => prevCount + 1);
        // Show success message
        const productSizeLabel = result.data?.alias_name || result.data?.id || '';
        showCreate(i18n.t('productSizes.notifications.created', { name: productSizeLabel }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showCreate]);

  /**
   * Update an existing product size
   */
  const updateProductSize = useCallback(async (productSizeId, productSizeData) => {
    setError(null);
    
    try {
      // Preserve stock-related fields if the form omitted them or sent null
      // Find existing item in local state to reuse its stock fields
      try {
        const existing = productSizes.find((ps) => String(ps.id) === String(productSizeId));
        if (existing) {
          // Only copy if the incoming payload doesn't include meaningful values
          if (productSizeData.hasOwnProperty('is_low_stock')) {
            if (productSizeData.is_low_stock === null || productSizeData.is_low_stock === undefined) {
              productSizeData.is_low_stock = existing.is_low_stock;
            }
          } else {
            productSizeData.is_low_stock = existing.is_low_stock;
          }

          if (productSizeData.hasOwnProperty('stock_status')) {
            if (productSizeData.stock_status === null || productSizeData.stock_status === undefined) {
              productSizeData.stock_status = existing.stock_status;
            }
          } else {
            productSizeData.stock_status = existing.stock_status;
          }
        }
      } catch (e) {
        // ignore merging errors and proceed with provided payload
      }

      const result = await productSizesService.updateProductSize(productSizeId, productSizeData);
      
      if (result.success) {
        // Update the product size in local state, preserving stock fields if server returned nulls
        setProductSizes(prevProductSizes => 
          prevProductSizes.map(productSize => {
            if (productSize.id === productSizeId) {
              const updatedData = { ...productSize, ...result.data };
              
              // If server returned null for stock fields, preserve the original values
              if (result.data.is_low_stock === null || result.data.is_low_stock === undefined) {
                updatedData.is_low_stock = productSize.is_low_stock;
              }
              if (result.data.stock_status === null || result.data.stock_status === undefined) {
                updatedData.stock_status = productSize.stock_status;
              }
              
              return updatedData;
            }
            return productSize;
          })
        );
        // Show success message
        const productSizeLabel = result.data?.alias_name || productSizeId;
        showUpdate(i18n.t('productSizes.notifications.updated', { name: productSizeLabel }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate, productSizes]);

  /**
   * Update stock for a specific product size
   */
  const updateProductSizeStock = useCallback(async (productSizeId, currentStock) => {
    setError(null);
    
    try {
      const result = await productSizesService.updateProductSizeStock(productSizeId, currentStock);
      
      if (result.success) {
        // Update the product size in local state
        setProductSizes(prevProductSizes => 
          prevProductSizes.map(productSize => 
            productSize.id === productSizeId ? { ...productSize, ...result.data } : productSize
          )
        );
        // Show success message
        const productSizeLabel = result.data?.alias_name || productSizeId;
        showUpdate(i18n.t('productSizes.notifications.stockUpdated', { name: productSizeLabel }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showUpdate]);

  /**
   * Delete a single product size
   */
  const deleteProductSize = useCallback(async (productSizeId) => {
    setError(null);
    
    try {
      const result = await productSizesService.deleteProductSize(productSizeId);
      
      if (result.success) {
        // Remove the product size from local state
        setProductSizes(prevProductSizes => prevProductSizes.filter(productSize => productSize.id !== productSizeId));
        setTotalCount(prevCount => Math.max(0, prevCount - 1));
        // Show success message
        showDelete(i18n.t('productSizes.notifications.deleted', { name: productSizeId }));
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      // Extract detail from axios error response
      const errorMessage = (() => {
        // Check if it's an axios error with response data
        if (err.response && err.response.data) {
          const data = err.response.data;
          if (typeof data === 'string') return data;
          if (data.detail) return data.detail;
          if (data.message) return data.message;
          if (data.error) return data.error;
        }
        // Fallback to error message or default
        return err.message;
      })();
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete]);

  /**
   * Delete multiple product sizes
   */
  const bulkDeleteProductSizes = useCallback(async (productSizeIds) => {
    if (!Array.isArray(productSizeIds) || productSizeIds.length === 0) {
      return { success: false, error: i18n.t('productSizes.errors.noSelection')};
    }

    setError(null);
    
    try {
      const result = await productSizesService.bulkDeleteProductSizes(productSizeIds);
      
      if (result.success) {
        // Remove the deleted product sizes from local state
        setProductSizes(prevProductSizes => 
          prevProductSizes.filter(productSize => !productSizeIds.includes(productSize.id))
        );
        setTotalCount(prevCount => Math.max(0, prevCount - productSizeIds.length));
        // Show success message
        const deletedCount = productSizeIds.length;
        // Try to map ids to names from the previous local state snapshot
        const deletedNames = [];
        // We can try to infer names from the result.data if the API returns them, else from previously stored productSizes
        if (result.data && Array.isArray(result.data)) {
          result.data.forEach(item => {
            if (item && (item.alias_name || item.name || item.id)) {
              deletedNames.push(item.alias_name || item.name || item.id);
            }
          });
        }
        if (deletedNames.length === 0) {
          // fallback: try to read names from productSizes state before deletion by matching ids
          // Note: we can't access previous state directly here; rely on available local array (could be stale)
          try {
            // capture current list (closure)
            const maybeNames = productSizes
              .filter(ps => productSizeIds.includes(ps.id))
              .map(ps => ps.alias_name || ps.name || ps.id);
            if (maybeNames.length) deletedNames.push(...maybeNames);
          } catch (e) {
            // ignore
          }
        }

        if (deletedNames.length) {
          const namesStr = deletedNames.join(', ');
          if (deletedNames.length === 1) {
            const msg = i18n.t('productSizes.notifications.bulkDeletedListSingle', { name: namesStr });
            showDelete(msg);
          } else {
            const msg = i18n.t('productSizes.notifications.bulkDeletedListMultiple', { names: namesStr });
            showDelete(msg);
          }
        } else {
          const bulkMsg = deletedCount > 1 ? i18n.t('productSizes.notifications.bulkDeletedMultiple', { count: String(deletedCount) }) : i18n.t('productSizes.notifications.bulkDeletedSingle');
          showDelete(bulkMsg);
        }
      } else {
        setError(result.error);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [showDelete, productSizes]);

  /**
   * Import product sizes from Excel file
   */
  const importProductSizes = useCallback(async (file) => {
    setLoading(true);
    // keep hook-level error separate from import result details
    // import errors (row-level) will be returned to the caller so the modal can display them
    
    try {
      const result = await productSizesService.importProductSizesFromExcel(file);
      
      if (result.success && result.data) {
        const resp = result.data;
        const errors = Array.isArray(resp.errors) ? resp.errors : [];
        const created = Array.isArray(resp.created_product_sizes) ? resp.created_product_sizes : (Array.isArray(resp.created) ? resp.created : []);
        const totalErrors = typeof resp.total_errors === 'number' ? resp.total_errors : errors.length;

        if (totalErrors > 0) {
          // Partial / failed import - return the array of error messages so the UI modal can display them
          // Do NOT set hook-level error (keeps global error dialog separate)
          return { success: false, error: errors.length ? errors : [`${totalErrors} rows failed to import`] , data: resp };
        }

        // All rows imported successfully
        await fetchProductSizes({}, { force: true });
  const importedCount = resp.total_created || created.length || 0;
  // Use localized string only (no inline fallback)
  showCreate(i18n.t('productSizes.notifications.imported', { count: String(importedCount) }));
        return { success: true, data: resp };
      }

      // result.success was false (network/server error)
      setError(result.error);
      return result;
    } catch (err) {
      const errorMessage = err.message;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [fetchProductSizes, showCreate]);

  /**
   * Export product sizes as Excel via the server-side download endpoint
   */
  const exportProductSizes = useCallback(async (params = {}) => {
    const result = await productSizesService.exportProductSizesToExcel(params);
    if (!result.success) {
      return { success: false, error: result.error };
    }

    const disposition = result.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    const filename = match ? match[1].replace(/['"]/g, '') : i18n.t('customers.export.filename');

    const blobUrl = URL.createObjectURL(result.data);
    const anchor = document.createElement('a');
    anchor.href = blobUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(blobUrl);

    return { success: true };
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    productSizes,
    loading,
    error,
    totalPages,
    totalCount,
    
    // Actions
    fetchProductSizes,
    getProductSizeById,
    createProductSize,
    updateProductSize,
    updateProductSizeStock,
    deleteProductSize,
    bulkDeleteProductSizes,
    importProductSizes,
    exportProductSizes,
    clearError,
  };
};

export default useProductSizes;
